import React, { forwardRef, ReactNode } from 'react';
import {
  Avatar as MuiAvatar,
  Button,
  Checkbox as MuiCheckbox,
  CircularProgress as MuiCircularProgress,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  List as MuiList,
  ListItem as MuiListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem as MuiMenuItem,
  Paper as MuiPaper,
  Step as MuiStep,
  StepLabel as MuiStepLabel,
  Stepper as MuiStepper,
  Switch,
  TextField as MuiTextField,
  Autocomplete,
  Grid,
  Typography,
  Icon,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell,
  TableFooter as MuiTableFooter,
  TableHead,
  TableRow as MuiTableRow,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

export { MuiThemeProvider } from '../mui-theme-provider';
export { default as getMuiTheme } from '../get-mui-theme';

type AnyProps = Record<string, unknown>;

function wrapClick(handler?: (e: React.MouseEvent) => void, disabled?: boolean) {
  return disabled ? undefined : handler;
}

export const FlatButton = forwardRef<HTMLButtonElement, AnyProps>(
  ({ label, children, primary, secondary, fullWidth, containerElement, style, onClick, disabled, ...rest }, ref) => {
    if (containerElement === 'label') {
      return (
        <label style={{ display: 'inline-block', cursor: disabled ? 'default' : 'pointer', ...style as object }}>
          <input type="file" style={{ display: 'none' }} {...rest} />
          <Button ref={ref} variant="text" fullWidth={fullWidth} disabled={disabled}>
            {label ?? children}
          </Button>
        </label>
      );
    }
    return (
      <Button
        ref={ref}
        variant="text"
        color={secondary ? 'secondary' : primary ? 'primary' : 'inherit'}
        fullWidth={fullWidth}
        style={style as React.CSSProperties}
        onClick={onClick as React.MouseEventHandler}
        disabled={disabled}
        {...rest}
      >
        {label ?? children}
      </Button>
    );
  },
);

export const RaisedButton = forwardRef<HTMLButtonElement, AnyProps>(
  ({ label, children, primary = true, fullWidth, style, onClick, disabled, type }, ref) => (
    <Button
      ref={ref}
      variant="contained"
      color={primary ? 'primary' : 'inherit'}
      fullWidth={fullWidth}
      style={style as React.CSSProperties}
      onClick={onClick as React.MouseEventHandler}
      disabled={disabled}
      type={type as 'button' | 'submit' | 'reset'}
    >
      {label ?? children}
    </Button>
  ),
);

export const FloatingActionButton = forwardRef<HTMLButtonElement, AnyProps>(
  ({ children, style, onClick, disabled, mini }, ref) => (
    <Fab ref={ref} size={mini ? 'small' : 'medium'} style={style as React.CSSProperties} onClick={onClick as React.MouseEventHandler} disabled={disabled} color="primary">
      {children}
    </Fab>
  ),
);

export const TextField = forwardRef<HTMLDivElement, AnyProps>(
  (
    {
      floatingLabelText,
      hintText,
      value,
      onChange,
      multiLine,
      rows,
      rowsMax,
      fullWidth,
      type,
      name,
      disabled,
      errorText,
      style,
    },
    ref,
  ) => (
    <MuiTextField
      ref={ref}
      label={floatingLabelText}
      placeholder={hintText}
      value={value ?? ''}
      onChange={(e) => onChange?.(e, e.target.value)}
      multiline={multiLine}
      minRows={rows}
      maxRows={rowsMax}
      fullWidth={fullWidth}
      type={type as string}
      name={name as string}
      disabled={disabled}
      error={!!errorText}
      helperText={errorText as string}
      margin="normal"
      style={style as React.CSSProperties}
      variant="standard"
    />
  ),
);

export const Checkbox = ({ label, checked, onCheck, style, disabled }: AnyProps) => {
  if (!label) {
    return (
      <MuiCheckbox
        checked={!!checked}
        onChange={(_, v) => onCheck?.(_, v)}
        disabled={disabled}
        style={style as React.CSSProperties}
        size="small"
      />
    );
  }

  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, ...(style as object) }}>
      <MuiCheckbox checked={!!checked} onChange={(_, v) => onCheck?.(_, v)} disabled={disabled} />
      <span>{label as ReactNode}</span>
    </label>
  );
};

export const Dialog = ({ open, onRequestClose, title, actions, children, modal, autoScrollBodyContent }: AnyProps) => (
  <MuiDialog open={!!open} onClose={onRequestClose} fullWidth maxWidth="sm">
    {title ? <DialogTitle>{title as ReactNode}</DialogTitle> : null}
    <DialogContent dividers={autoScrollBodyContent}>{children as ReactNode}</DialogContent>
    {actions ? <DialogActions>{actions as ReactNode}</DialogActions> : null}
  </MuiDialog>
);

export const List = ({ children, style }: AnyProps) => <MuiList style={style as React.CSSProperties}>{children as ReactNode}</MuiList>;

export const ListItem = ({
  primaryText,
  secondaryText,
  leftAvatar,
  leftCheckbox,
  rightIconButton,
  onClick,
  style,
  disabled,
  children,
}: AnyProps) => {
  const hasPrimaryText = primaryText !== undefined && primaryText !== '';
  const hasSecondaryText = secondaryText !== undefined && secondaryText !== '';

  return (
    <MuiListItem
      onClick={wrapClick(onClick as (e: React.MouseEvent) => void, disabled as boolean)}
      style={style as React.CSSProperties}
      secondaryAction={rightIconButton as ReactNode}
      disabled={disabled}
    >
      {leftCheckbox ? (
        <ListItemIcon sx={{ minWidth: 36 }}>{leftCheckbox as ReactNode}</ListItemIcon>
      ) : null}
      {leftAvatar as ReactNode}
      {hasPrimaryText || hasSecondaryText ? (
        <ListItemText primary={primaryText as ReactNode} secondary={secondaryText as ReactNode} />
      ) : null}
      {children as ReactNode}
    </MuiListItem>
  );
};

export const Subheader = ({ children, style }: AnyProps) => (
  <ListSubheader style={style as React.CSSProperties}>{children as ReactNode}</ListSubheader>
);

export const Avatar = ({ src, size, style, children }: AnyProps) => (
  <MuiAvatar src={src as string} sx={{ width: size, height: size, ...(style as object) }}>
    {children as ReactNode}
  </MuiAvatar>
);

export const Paper = ({ children, style, zDepth }: AnyProps) => (
  <MuiPaper elevation={typeof zDepth === 'number' ? zDepth : 1} style={style as React.CSSProperties}>
    {children as ReactNode}
  </MuiPaper>
);

export const CircularProgress = ({ size, style }: AnyProps) => (
  <MuiCircularProgress size={size as number} style={style as React.CSSProperties} />
);

export const FontIcon = ({ className, style, color, onClick, ...rest }: AnyProps) => (
  <Icon
    className={className as string}
    style={{ ...(style as object), color: color as string }}
    // Important: preserve click handlers coming from `src/common/components/font-awesome.js`
    onClick={onClick as React.MouseEventHandler}
    {...rest}
  />
);

export const Stepper = ({ activeStep, children, style }: AnyProps) => (
  <MuiStepper activeStep={activeStep as number} style={style as React.CSSProperties}>
    {children as ReactNode}
  </MuiStepper>
);

export const Step = ({ children }: AnyProps) => <MuiStep>{children as ReactNode}</MuiStep>;

export const StepLabel = ({ children }: AnyProps) => <MuiStepLabel>{children as ReactNode}</MuiStepLabel>;

export const MenuItem = ({ value, primaryText, children, onClick }: AnyProps) => (
  <MuiMenuItem value={value} onClick={onClick as React.MouseEventHandler}>
    {primaryText ?? children}
  </MuiMenuItem>
);

export const SelectField = ({ value, onChange, children, floatingLabelText, fullWidth, style }: AnyProps) => (
  <MuiTextField
    select
    label={floatingLabelText as string}
    value={value ?? ''}
    onChange={(e) => onChange?.(null, null, e.target.value)}
    fullWidth={fullWidth}
    margin="normal"
    style={style as React.CSSProperties}
    variant="standard"
  >
    {children as ReactNode}
  </MuiTextField>
);

export const Toggle = ({ toggled, onToggle, label, style }: AnyProps) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: 8, ...(style as object) }}>
    <Switch checked={!!toggled} onChange={(_, v) => onToggle?.(_, v)} />
    <span>{label as ReactNode}</span>
  </label>
);

export const DatePickerLegacy = ({ value, onChange, floatingLabelText, fullWidth, style }: AnyProps) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      label={floatingLabelText as string}
      value={value ? dayjs(value as string | Date) : null}
      onChange={(d: Dayjs | null) => onChange?.(null, d?.toDate())}
      slotProps={{ textField: { fullWidth, margin: 'normal', style: style as React.CSSProperties, variant: 'standard' } }}
    />
  </LocalizationProvider>
);

export { DatePickerLegacy as DatePicker };

export { default as Drawer } from './Drawer';

export const Table = ({ children, style }: AnyProps) => (
  <MuiTable style={style as React.CSSProperties}>{children as ReactNode}</MuiTable>
);

export const TableBody = ({ children, displayRowCheckbox }: AnyProps) => (
  <MuiTableBody>{children as ReactNode}</MuiTableBody>
);

export const TableHeader = ({ children, displaySelectAll, adjustForCheckbox, style }: AnyProps) => (
  <TableHead style={style as React.CSSProperties}>{children as ReactNode}</TableHead>
);

export const TableHeaderColumn = ({ children, style }: AnyProps) => (
  <TableCell style={style as React.CSSProperties}>{children as ReactNode}</TableCell>
);

export const TableRow = ({ children, style }: AnyProps) => (
  <MuiTableRow style={style as React.CSSProperties}>{children as ReactNode}</MuiTableRow>
);

export const TableRowColumn = ({ children, style }: AnyProps) => (
  <TableCell style={style as React.CSSProperties}>{children as ReactNode}</TableCell>
);

export const TableFooter = ({ children, style }: AnyProps) => (
  <MuiTableFooter style={style as React.CSSProperties}>{children as ReactNode}</MuiTableFooter>
);

export const AutoComplete = ({
  dataSource,
  searchText,
  onUpdateInput,
  onNewRequest,
  floatingLabelText,
  fullWidth,
  filter,
}: AnyProps) => (
  <Autocomplete
    freeSolo
    options={(dataSource as string[]) ?? []}
    inputValue={(searchText as string) ?? ''}
    onInputChange={(_, v) => onUpdateInput?.(v)}
    onChange={(_, v) => onNewRequest?.(v, -1)}
    filterOptions={(opts, state) =>
      filter ? opts.filter((o) => (filter as (q: string, key: string) => boolean)(state.inputValue, o)) : opts
    }
    renderInput={(params) => (
      <MuiTextField {...params} label={floatingLabelText as string} fullWidth={fullWidth} margin="normal" variant="standard" />
    )}
  />
);

export const GridList = ({ children, style, cols, cellHeight, padding }: AnyProps) => (
  <Grid container spacing={Number(padding) || 2} style={style as React.CSSProperties}>
    {React.Children.map(children as ReactNode, (child) => (
      <Grid item xs={12} sm={6} md={12 / (Number(cols) || 4)}>
        {child}
      </Grid>
    ))}
  </Grid>
);

export const GridTile = ({ children, title, actionIcon, subtitle }: AnyProps) => (
  <MuiPaper sx={{ p: 1, position: 'relative' }}>
    {actionIcon as ReactNode}
    {children as ReactNode}
    <Typography variant="caption" display="block">{title as ReactNode}</Typography>
    {subtitle ? <Typography variant="caption" color="text.secondary">{subtitle as ReactNode}</Typography> : null}
  </MuiPaper>
);

export const Toolbar = ({ children, style }: AnyProps) => (
  <div style={{ display: 'flex', alignItems: 'center', minHeight: 56, padding: '0 16px', ...(style as object) }}>
    {children as ReactNode}
  </div>
);

export const ToolbarGroup = ({ children, style }: AnyProps) => (
  <div style={{ display: 'flex', alignItems: 'center', flex: 1, ...(style as object) }}>{children as ReactNode}</div>
);

export const ToolbarTitle = ({ text, style }: AnyProps) => (
  <Typography variant="h6" style={style as React.CSSProperties}>{text as ReactNode}</Typography>
);
